import { Box } from "@mui/material";
import React, { RefObject, useCallback, useRef, useState } from "react";
import { NAVBAR_HEIGHT } from "../../../components/Navbar/NavDesktop";
import ScormPlayer from "../../../components/Scorm/scormPlayer";
import { gray } from "../../../theme/themePrimitives";
import { PdfContentHeader } from "./PdfContentHeader";
import { PdfPageByPageViewer } from "./PdfPageByPageViewer";
import { PdfViewer, PdfViewerHandle, SearchMatchStats } from "./PdfViewer";
import { VideoPlayer } from "./VideoPlayer";
import useScreenSize from "../../../hooks/useScreenSize";

interface CourseContentProps {
  assignmentId: string;
  setMaxChapter: React.Dispatch<React.SetStateAction<string>>;
  maxChapter: RefObject<string>;
  iframeRef: React.RefObject<HTMLIFrameElement | null>;
  assignmentUrl: string;
  assignmentTitle?: string;
  isPdf?: boolean;
  pageByPage?: boolean;
  pdfInitialPage?: number;
  onPdfDocumentLoad?: (numPages: number) => void;
  onPdfReachedEnd?: () => void;
  onPdfPageChange?: (page: number) => void;
  isVideo?: boolean;
  videoInitialTime?: number;
  onVideoProgress?: (currentTime: number, duration: number) => void;
  onVideoReachedEnd?: () => void;
  isVideoMandatory?: boolean;
  videoMandatoryAlreadySatisfied?: boolean;
  requiresAcknowledgement?: boolean;
  fillParent?: boolean;
  onAttemptsExceededChange?: (exceeded: boolean) => void;
}

// Real PDF uploads may come back as an absolute S3/CDN URL rather than a
// path relative to this app's own origin (the convention SCORM content
// relies on); only prepend "/" when the value isn't already an absolute URL.
const resolveAssetUrl = (url: string) =>
  /^https?:\/\//i.test(url) ? url : `/${url}`;

export const CourseContent: React.FC<CourseContentProps> = ({
  assignmentId,
  setMaxChapter,
  maxChapter,
  iframeRef,
  assignmentUrl,
  assignmentTitle,
  isPdf,
  pageByPage,
  pdfInitialPage,
  onPdfDocumentLoad,
  onPdfReachedEnd,
  onPdfPageChange,
  isVideo,
  videoInitialTime,
  onVideoProgress,
  onVideoReachedEnd,
  isVideoMandatory,
  videoMandatoryAlreadySatisfied,
  requiresAcknowledgement,
  fillParent = false,
  onAttemptsExceededChange,
}) => {
  const [numPages, setNumPages] = useState<number | null>(null);
  const [visiblePage, setVisiblePage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchStats, setSearchStats] = useState<SearchMatchStats>({
    total: 0,
    current: 0,
  });
  const pdfViewerRef = useRef<PdfViewerHandle>(null);

  const handleDocumentLoad = (loadedNumPages: number) => {
    setNumPages(loadedNumPages);
    onPdfDocumentLoad?.(loadedNumPages);
  };

  const handleVisiblePageChange = useCallback((page: number) => {
    setVisiblePage(page);
  }, []);

  const { isTablet } = useScreenSize();

  return (
    <Box
      sx={{
        minHeight: 0,
        minWidth: 0,
        position: "relative",
        ...(fillParent && {
          flex: 1,
          display: "flex",
          flexDirection: "column",
        }),
      }}
    >
      {isPdf ? (
        <Box
          sx={{
            height: fillParent
              ? "100%"
              : `calc(100vh - ${isTablet ? "200px" : NAVBAR_HEIGHT})`,
            ...(!fillParent && {
              "@supports (height: 100dvh)": {
                height: `calc(100dvh - ${isTablet ? "200px" : NAVBAR_HEIGHT})`,
              },
            }),
            ...(fillParent && { flex: 1, minHeight: 0 }),
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {pageByPage ? (
            <PdfPageByPageViewer
              assignmentTitle={assignmentTitle ?? ""}
              url={resolveAssetUrl(assignmentUrl)}
              initialPage={pdfInitialPage}
              onPageChange={onPdfPageChange}
              onDocumentLoad={handleDocumentLoad}
              onReachedEnd={onPdfReachedEnd}
              requiresAcknowledgement={requiresAcknowledgement}
            />
          ) : (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                height: "100%",
                minHeight: 0,
                border: `1px solid ${gray[200]}`,
                backgroundColor: "white",
                overflow: "hidden",
              }}
            >
              <PdfContentHeader
                title={assignmentTitle}
                currentPage={visiblePage}
                numPages={numPages}
                searchTerm={searchTerm}
                onSearchTermChange={setSearchTerm}
                matchCount={searchStats.total}
                activeMatchIndex={searchStats.current}
                onNextMatch={() => pdfViewerRef.current?.goToNextMatch()}
                onPrevMatch={() => pdfViewerRef.current?.goToPrevMatch()}
                onClearSearch={() => setSearchTerm("")}
              />
              <Box sx={{ flex: 1, minHeight: 0 }}>
                <PdfViewer
                  ref={pdfViewerRef}
                  url={resolveAssetUrl(assignmentUrl)}
                  searchTerm={searchTerm}
                  onSearchMatchesChange={setSearchStats}
                  onDocumentLoad={handleDocumentLoad}
                  onReachedEnd={onPdfReachedEnd}
                  onVisiblePageChange={handleVisiblePageChange}
                />
              </Box>
            </Box>
          )}
        </Box>
      ) : isVideo ? (
        <Box
          sx={{
            height: fillParent
              ? "100%"
              : `calc(100vh - ${isTablet ? "151px" : NAVBAR_HEIGHT})`,
            ...(!fillParent && {
              "@supports (height: 100dvh)": {
                height: `calc(100dvh - ${isTablet ? "151px" : NAVBAR_HEIGHT})`,
              },
            }),
            ...(fillParent && { flex: 1, minHeight: 0 }),
            border: `1px solid ${gray[200]}`,
            overflow: "hidden",
          }}
        >
          <VideoPlayer
            src={resolveAssetUrl(assignmentUrl)}
            title={assignmentTitle}
            initialTime={videoInitialTime}
            onProgress={onVideoProgress}
            onReachedEnd={onVideoReachedEnd}
            isMandatory={isVideoMandatory}
            mandatoryAlreadySatisfied={videoMandatoryAlreadySatisfied}
          />
        </Box>
      ) : (
        <ScormPlayer
          assignmentUrl={assignmentUrl}
          assignmentId={assignmentId}
          iframeRef={iframeRef}
          setMaxChapter={setMaxChapter}
          maxChapter={maxChapter}
          fillParent={fillParent}
          onAttemptsExceededChange={onAttemptsExceededChange}
        />
      )}
    </Box>
  );
};
